import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid2 as Grid, Paper, Typography, CircularProgress, Alert, Chip, Stack,
  LinearProgress, Avatar, Divider,
} from '@mui/material';
import {
  AttachMoney, Receipt, People, Business,
  TrendingUp, TrendingDown, CheckCircle, Warning, Error as ErrorIcon,
  Schedule, BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { formatCurrency, formatNumber } from '../../utils/mockData';
import dashboardService, { AdminDashboardData } from '../../services/dashboard.service';

// Paleta Looker Studio
const COLORS = {
  primary: '#6B2FA0',
  secondary: '#4285F4',
  success: '#0F9D58',
  warning: '#F4B400',
  danger: '#DB4437',
  info: '#00ACC1',
  muted: '#9E9E9E',
};

const PIE_COLORS = ['#6B2FA0', '#4285F4', '#0F9D58', '#F4B400', '#DB4437', '#00ACC1', '#9C27B0', '#FF5722', '#607D8B', '#795548'];

const STATUS_META: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  PAID: { label: 'Pago', color: COLORS.success, icon: <CheckCircle fontSize="small" /> },
  PENDING: { label: 'Pendente', color: COLORS.warning, icon: <Schedule fontSize="small" /> },
  FAILED: { label: 'Falhou', color: COLORS.danger, icon: <ErrorIcon fontSize="small" /> },
  CANCELED: { label: 'Cancelado', color: COLORS.muted, icon: <Warning fontSize="small" /> },
};

interface KpiCardProps {
  title: string;
  value: string;
  subtitle?: string;
  trend?: number;
  icon: React.ReactElement;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, trend, icon, color }) => {
  const isPositive = (trend ?? 0) >= 0;
  return (
    <Paper sx={{ p: 2.5, height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar sx={{ bgcolor: `${color}15`, color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 0.5, color: 'text.primary' }}>
            {value}
          </Typography>
          {(trend !== undefined || subtitle) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {trend !== undefined && (
                <Chip
                  size="small"
                  icon={isPositive ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
                  label={`${isPositive ? '+' : ''}${trend.toFixed(1)}%`}
                  sx={{
                    height: 22,
                    bgcolor: isPositive ? `${COLORS.success}15` : `${COLORS.danger}15`,
                    color: isPositive ? COLORS.success : COLORS.danger,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
              {subtitle && (
                <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

interface SectionCardProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, subtitle, action, children, minHeight = 320 }) => (
  <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', minHeight }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

const formatShortDate = (iso: string) => {
  try {
    // iso é um dia 'YYYY-MM-DD' (balde já em dia de CV) → ler em UTC, não no fuso do browser.
    const d = new Date(iso);
    return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
  } catch { return iso; }
};

const DashboardBackoffice: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminDashboardData | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true); setError(null);
      const d = await dashboardService.getAdminDashboard();
      setData(d);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message || t('dashboard.errorLoadingDashboard'));
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary">{t('dashboard.loadingDashboard')}</Typography>
      </Box>
    );
  }
  if (error || !data) {
    return <Box><Alert severity="error">{error || t('dashboard.errorLoadingDashboard')}</Alert></Box>;
  }

  const { stats, revenueTimeline, servicesBreakdown, statusBreakdown, topEntities, hourlyPattern, deviceBreakdown = [], deviceTimeline = [] } = data;
  const timelineFormatted = revenueTimeline.map(r => ({ ...r, dateLabel: formatShortDate(r.date) }));
  const maxTopEntityRevenue = Math.max(...topEntities.map(e => e.revenue), 1);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', px: { xs: 2, md: 3 }, py: 3, mx: { xs: -2, md: -3 }, my: -3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} sx={{ color: 'text.primary' }}>
          Dashboard Administrativo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Últimos 30 dias • Atualização em tempo real
        </Typography>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Receita (30d)"
            value={formatCurrency(stats.receita_mes)}
            trend={stats.receita_trend}
            subtitle="vs 30d anteriores"
            icon={<AttachMoney />}
            color={COLORS.primary}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Transações (30d)"
            value={formatNumber(stats.transacoes_mes)}
            trend={stats.transacoes_trend}
            subtitle={`${stats.taxa_sucesso}% taxa sucesso`}
            icon={<Receipt />}
            color={COLORS.secondary}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Ticket Médio"
            value={formatCurrency(stats.ticket_medio)}
            trend={stats.ticket_trend}
            subtitle="por transação"
            icon={<BarChartIcon />}
            color={COLORS.success}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Utilizadores Ativos"
            value={formatNumber(stats.utilizadores_ativos)}
            trend={stats.utilizadores_trend}
            subtitle={`de ${formatNumber(stats.total_utilizadores)} total`}
            icon={<People />}
            color={COLORS.info}
          />
        </Grid>
      </Grid>

      {/* Cards secundários */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Entidades"
            value={formatNumber(stats.total_entidades)}
            subtitle="cadastradas"
            icon={<Business />}
            color={COLORS.warning}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Novos Utilizadores (30d)"
            value={formatNumber(stats.novos_utilizadores_mes)}
            trend={stats.novos_utilizadores_trend}
            subtitle="novos registos"
            icon={<People />}
            color={COLORS.info}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Status das Transações
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Últimos 30 dias</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {statusBreakdown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              ) : (
                statusBreakdown.map((s) => {
                  const meta = STATUS_META[s.status] || { label: s.status, color: COLORS.muted, icon: <Warning fontSize="small" /> };
                  return (
                    <Box key={s.status} flex="1 1 140px">
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">{meta.label}</Typography>
                      </Stack>
                      <Typography variant="h6" fontWeight={700}>{formatNumber(s.count)}</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={s.percentage}
                        sx={{
                          mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'action.selected',
                          '& .MuiLinearProgress-bar': { bgcolor: meta.color, borderRadius: 3 },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {s.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Revenue Timeline + Services Breakdown */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard title="Receita Diária" subtitle="Últimos 30 dias">
            {timelineFormatted.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados no período</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280} minWidth={0} debounce={50}>
                <AreaChart data={timelineFormatted}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                    formatter={(value: any) => formatCurrency(value as number)}
                    labelFormatter={(label: any) => `Dia ${label}`}
                  />
                  <Area type="monotone" dataKey="revenue" stroke={COLORS.primary} strokeWidth={2.5} fill="url(#revenueGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard title="Por Tipo de Serviço" subtitle={`${servicesBreakdown.length} tipos`}>
            {servicesBreakdown.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180} minWidth={0} debounce={50}>
                  <PieChart>
                    <Pie data={servicesBreakdown as any} dataKey="transactions" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
                      {servicesBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value: any) => formatNumber(value)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {servicesBreakdown.slice(0, 5).map((s, i) => (
                    <Stack key={s.code} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                      <Typography variant="caption" fontWeight={700} color="text.primary">{s.percentage}%</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Top Entidades + Padrão Horário */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title="Top 10 Entidades" subtitle="Por receita nos últimos 30 dias">
            {topEntities.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {topEntities.map((e, i) => {
                  const pct = (e.revenue / maxTopEntityRevenue) * 100;
                  return (
                    <Box key={i}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                          <Avatar sx={{
                            width: 26, height: 26, fontSize: '0.75rem', fontWeight: 700,
                            bgcolor: i === 0 ? `${COLORS.primary}20` : i === 1 ? `${COLORS.secondary}20` : i === 2 ? `${COLORS.success}20` : 'action.selected',
                            color: i === 0 ? COLORS.primary : i === 1 ? COLORS.secondary : i === 2 ? COLORS.success : 'text.secondary',
                          }}>{i + 1}</Avatar>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {e.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{formatNumber(e.transactions)} tx</Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight={700} sx={{ ml: 2, color: 'text.primary' }}>
                          {formatCurrency(e.revenue)}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 5, borderRadius: 3, bgcolor: 'action.selected',
                          '& .MuiLinearProgress-bar': { bgcolor: COLORS.primary, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard title="Padrão Horário" subtitle="Distribuição por hora (últimos 7 dias)">
            {hourlyPattern.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280} minWidth={0} debounce={50}>
                <BarChart data={hourlyPattern}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} interval={1} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                  <Bar dataKey="count" fill={COLORS.secondary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Web vs Mobile */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard title="Plataforma de Acesso" subtitle="Web · Mobile App · Pagamentos SISP (30 dias)">
            {deviceBreakdown.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180} minWidth={0} debounce={50}>
                  <PieChart>
                    <Pie data={deviceBreakdown} dataKey="count" nameKey="device" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
                      {deviceBreakdown.map((d) => {
                        const c = d.device === 'Web' ? COLORS.secondary : d.device === 'Mobile App' ? COLORS.primary : COLORS.muted;
                        return <Cell key={d.device} fill={c} />;
                      })}
                    </Pie>
                    <Tooltip formatter={(value: any) => formatNumber(value)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {deviceBreakdown.map((d) => {
                    const c = d.device === 'Web' ? COLORS.secondary : d.device === 'Mobile App' ? COLORS.primary : COLORS.muted;
                    return (
                      <Stack key={d.device} direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{d.device}</Typography>
                        <Typography variant="body2" fontWeight={700}>{d.percentage}%</Typography>
                        <Typography variant="caption" color="text.secondary">{formatNumber(d.count)} tx</Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </>
            )}
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard title="Transações por Device" subtitle="Web vs Mobile App — últimos 30 dias">
            {deviceTimeline.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography variant="body2" color="text.secondary">Sem dados</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={280} minWidth={0} debounce={50}>
                <BarChart data={deviceTimeline} barSize={8}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} interval={4} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="web" name="Web" fill={COLORS.secondary} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="mobile" name="Mobile" fill={COLORS.primary} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Insights de decisão */}
      <SectionCard title="Insights" subtitle="Análise automática para tomada de decisões" minHeight={0}>
        <Grid container spacing={2}>
          {stats.receita_trend > 10 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="success" icon={<TrendingUp />}>
                <strong>Crescimento saudável:</strong> receita +{stats.receita_trend.toFixed(1)}% vs período anterior. Continue a estratégia atual.
              </Alert>
            </Grid>
          )}
          {stats.receita_trend < -10 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="error" icon={<TrendingDown />}>
                <strong>Alerta:</strong> receita caiu {Math.abs(stats.receita_trend).toFixed(1)}%. Investigue os top serviços e entidades.
              </Alert>
            </Grid>
          )}
          {stats.taxa_sucesso < 80 && stats.taxa_sucesso > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="warning" icon={<Warning />}>
                <strong>Taxa de sucesso baixa:</strong> {stats.taxa_sucesso}%. Verifique falhas SISP e métodos de pagamento.
              </Alert>
            </Grid>
          )}
          {stats.taxa_sucesso >= 95 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="success" icon={<CheckCircle />}>
                <strong>Excelente taxa de sucesso:</strong> {stats.taxa_sucesso}% das transações processadas com sucesso.
              </Alert>
            </Grid>
          )}
          {servicesBreakdown.length > 0 && servicesBreakdown[0].percentage > 50 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="info">
                <strong>Concentração:</strong> <b>{servicesBreakdown[0].name}</b> representa {servicesBreakdown[0].percentage}% das transações. Considere diversificar a oferta.
              </Alert>
            </Grid>
          )}
          {stats.utilizadores_ativos > 0 && stats.total_utilizadores > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Alert severity="info">
                <strong>Engagement:</strong> {((stats.utilizadores_ativos / stats.total_utilizadores) * 100).toFixed(1)}% dos utilizadores ({stats.utilizadores_ativos} de {stats.total_utilizadores}) fizeram pelo menos 1 transação no período.
              </Alert>
            </Grid>
          )}
        </Grid>
      </SectionCard>
    </Box>
  );
};

export default DashboardBackoffice;
